import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import {
  api,
  ApiError,
  Severity,
  type DashboardFinding,
  type DashboardObligation,
  type DashboardOverview,
  type DashboardRegistry,
  type DashboardRunwayCell,
  type DashboardSite,
} from '../api/client'
import { Tile } from '../components/Tile'
import { cell } from '../components/countMark'
import './Dashboard.css'

/**
 * The landing page: what is coming, what is recorded, and how far to trust it.
 *
 * The register answers "what is the state of vehicle X". Nobody could answer
 * "what should we do this month" without reading 612 rows, and these numbers
 * existed but were never added up.
 *
 * Two rules shape everything here.
 *
 * **Every figure is a link.** A number a reader cannot open is a number they
 * cannot check, and the register already filters by obligation and severity — so
 * clicking 606 on the Insurance row shows exactly those 606 vehicles.
 *
 * **The red is not what it looks like.** The register reports 606 expired
 * insurance rows, and 493 of them lapsed inside one six-month window: a stale
 * spreadsheet, not 606 missed renewals. CLAUDE.md forbids quoting that figure as
 * fact, so the confidence panel is not a footnote — it sits above the fold and the
 * API computes it rather than the page asserting it.
 */

/** Filters the register understands. Kept here so a link cannot invent a param. */
function registerLink(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v))
  }
  return `/register?${q.toString()}`
}

/**
 * The window a runway column covers, as ISO dates.
 *
 * The start is clamped to the as-of date, never the 1st, because the runway
 * counts only what is still ahead. Without the clamp, clicking the current month
 * opens vehicles that already expired earlier in it — the chart says "3 due" and
 * the register shows 4, one of which lapsed a week ago. A forecast that includes
 * the past is not a forecast.
 *
 * Built from the parts rather than a Date roundtrip: `new Date(iso)` on a bare
 * yyyy-MM-dd is parsed as UTC and read back in local time, landing on the
 * previous month for anyone east of Greenwich — which is every user of this
 * register.
 */
function monthRange(iso: string, asOf: string): [string, string] {
  const [y, m] = iso.slice(0, 10).split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const mm = String(m).padStart(2, '0')

  const first = `${y}-${mm}-01`
  const from = asOf > first ? asOf.slice(0, 10) : first

  return [from, `${y}-${mm}-${String(last).padStart(2, '0')}`]
}

/** Day-first, as every other date on this site is. */
function monthLabel(iso: string): { m: string; y: string } {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  return {
    m: d.toLocaleDateString('en-IN', { month: 'short' }),
    y: String(d.getFullYear()).slice(2),
  }
}

export function Dashboard() {
  const [params] = useSearchParams()

  const overview = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
    // The numbers move on an import or an edit, not second to second.
    staleTime: 60_000,
  })

  /*
    Filtered-register links are a shipped feature — "a filtered register is a
    shareable link" — and they were written when the register lived at `/`. Those
    URLs are in emails and WhatsApp today. Landing them on the dashboard would
    drop the filter silently: the reader sees a plausible screen and never learns
    their link stopped working.

    Kept indefinitely. There is no way to enumerate who has one.
  */
  const REGISTER_PARAMS = [
    'page', 'pageSize', 'siteId', 'vehicleTypeId', 'search', 'worstSeverity',
    'hasSeverity', 'obligationTypeId', 'obligationSeverity', 'siteIds', 'source',
    'onlyOverridden', 'documentFilter', 'documentTypeId', 'sortBy', 'sortDescending',
    'expiryFrom', 'expiryTo',
  ]

  if (REGISTER_PARAMS.some((k) => params.has(k))) {
    return <Navigate to={`/register?${params.toString()}`} replace />
  }

  if (overview.isLoading) {
    return (
      <main className="dash">
        <p className="dash__msg">Loading the fleet…</p>
      </main>
    )
  }

  if (overview.isError || !overview.data) {
    const e = overview.error as ApiError | undefined
    return (
      <main className="dash">
        <p className="dash__msg dash__msg--bad">
          {e?.isOffline
            ? 'You are offline, so these figures could not be loaded.'
            : `The dashboard could not be loaded. ${e?.message ?? ''}`}
        </p>
        <Link className="dash__back" to="/register">
          Go to the register
        </Link>
      </main>
    )
  }

  return <DashboardBody data={overview.data} />
}

function DashboardBody({ data }: { data: DashboardOverview }) {
  const t = data.totals!
  const obligations = data.obligations ?? []
  const findings = data.findings ?? []

  // Which obligations are safe to act on, by name — used to caption the table
  // rather than making the reader cross-reference the confidence panel.
  const suspect = useMemo(
    () => new Set((data.staleness ?? []).filter((s) => s.isStaleSnapshot).map((s) => s.complianceTypeId)),
    [data.staleness],
  )

  return (
    <main className="dash">
      <header className="dash__head">
        <div>
          <p className="eyebrow">Fleet Due Date</p>
          <h1 className="dash__title">Fleet at a glance</h1>
        </div>
        <div className="dash__meta">
          <strong className="mono">{t.vehicleCount}</strong> vehicles · {t.siteCount} sites
          <br />
          as at <span className="mono">{formatDate(data.asOfDate)}</span>
        </div>
      </header>

      {/* The one thing that must be read before believing any red number. */}
      {findings.some((f) => f.kind === 'staleness' || f.kind === 'stale') && (
        <p className="dash__warn">
          <span aria-hidden="true">⚠</span>
          <span>
            <b>Not every red number means a missed renewal.</b> Several columns came from
            one spreadsheet import and have not been touched since — see{' '}
            <a href="#trust">how far to trust it</a> below.
          </span>
        </p>
      )}

      <section className="dash__sec">
        <div className="tiles">
          <Tile
            label="Vehicles"
            value={t.vehicleCount}
            tone="ok"
            href="/register"
          />
          {/* Each tile counts VEHICLES, matching what its link opens. The API's
              totals are per obligation — 1,194 unknown obligations sit across
              only 20 vehicles — and a tile that printed the obligation count
              while opening a vehicle list would contradict itself on click. */}
          <Tile
            label="Vehicles due soon"
            value={t.vehiclesDueSoon}
            note={`${t.dueSoonCount} obligations within 90 days`}
            tone="warn"
            href={registerLink({ hasSeverity: Severity.DueSoon })}
          />
          <Tile
            label="No certificate on file"
            value={t.vehiclesWithoutDocument}
            note={`only ${t.documentCount} scans exist`}
            tone="crit"
            href={registerLink({ documentFilter: 2 })}
          />
          <Tile
            label="Vehicles with a blank"
            value={t.vehiclesWithUnknown}
            note={`${t.unknownCount} blanks across all obligations`}
            tone="unk"
            href={registerLink({ hasSeverity: Severity.Unknown })}
          />
        </div>
      </section>

      <RunwayPanel
        runway={data.runway ?? []}
        monthsAhead={data.monthsAhead ?? 12}
        expired={t.expiredCount ?? 0}
        asOfDate={data.asOfDate ?? ''}
      />

      <ObligationPanel obligations={obligations} suspect={suspect} />

      <SitePanel sites={data.sites ?? []} />

      {/* Absent until a sweep has run, rather than drawn empty. */}
      {data.registry && <RegistryPanel registry={data.registry} />}

      <TrustPanel findings={findings} data={data} />

      <footer className="dash__foot">
        <p>
          Every figure is measured against the register as it stands, and every number
          above opens the vehicles behind it.
        </p>
        <p>
          <strong>There are no trend lines on this page.</strong> The register holds
          almost no history, so a chart of compliance over time would be invented rather
          than measured. What is shown ahead is a forecast from recorded expiry dates.
        </p>
      </footer>
    </main>
  )
}

/**
 * Twelve monthly columns — what falls due, and when.
 *
 * Plain columns rather than a stack split by obligation: a stacked chart needs
 * seven categorical colours, and this palette reserves colour for severity. Fitness
 * would end up permanently red. The month's dominant obligation is named under the
 * bar instead, which carries the same information without a second colour language.
 */
function RunwayPanel({
  runway,
  monthsAhead,
  expired,
  asOfDate,
}: {
  runway: DashboardRunwayCell[]
  monthsAhead: number
  expired: number
  /** Clamps the current month's window, so a forecast never includes the past. */
  asOfDate: string
}) {
  const months = useMemo(() => {
    const byOffset = new Map<number, DashboardRunwayCell[]>()
    for (const c of runway) {
      const list = byOffset.get(c.monthOffset ?? 0) ?? []
      list.push(c)
      byOffset.set(c.monthOffset ?? 0, list)
    }

    return [...byOffset.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([offset, cells]) => {
        const total = cells.reduce((n, c) => n + (c.expiringCount ?? 0), 0)
        const top = [...cells].sort((a, b) => (b.expiringCount ?? 0) - (a.expiringCount ?? 0))[0]
        return {
          offset,
          monthStart: cells[0].monthStart ?? '',
          total,
          // Named only when it genuinely dominates; "mixed" is honest for a month
          // split evenly, and claiming a leader there would misdescribe the work.
          lead: total > 0 && (top?.expiringCount ?? 0) / total >= 0.6 ? top : undefined,
        }
      })
  }, [runway])

  const peak = Math.max(1, ...months.map((m) => m.total))
  const total = months.reduce((n, m) => n + m.total, 0)

  return (
    <section className="dash__sec">
      <div className="dash__sech">
        <h2>What is coming</h2>
        <span className="eyebrow">next {monthsAhead} months</span>
      </div>
      <p className="dash__cap">
        Renewals falling due each month. Click a month to see the vehicles behind it.
      </p>

      <div className="dash__card">
        <div className="dashrun__scroll">
          <div className="dashrun">
            {months.map((m) => {
              const { m: mon, y } = monthLabel(m.monthStart)
              const pct = (m.total / peak) * 100

              const inner = (
                <>
                  <span className={`dashrun__n${m.total === 0 ? ' dashrun__n--zero' : ''}`}>
                    {m.total}
                  </span>
                  <span className="dashrun__plot">
                    {m.total > 0 ? (
                      <span className="dashrun__bar" style={{ height: `${pct}%` }} />
                    ) : (
                      <span className="dashrun__bar dashrun__bar--zero" />
                    )}
                  </span>
                  <span className="dashrun__m">
                    {mon}
                    <span className="dashrun__y">{y}</span>
                  </span>
                  <span className="dashrun__lead">
                    {m.total === 0 ? '' : (m.lead?.displayName ?? 'mixed')}
                  </span>
                </>
              )

              const title = `${mon} 20${y} — ${m.total} due${
                m.lead ? `, mostly ${m.lead.displayName}` : ''
              }`

              // The real month, not "due soon". The grid now takes an expiry
              // window, so clicking October opens exactly October's renewals
              // rather than the whole 90-day band around it.
              //
              // Unscoped by obligation on purpose: the column is every
              // obligation falling due that month, and narrowing to the leader
              // would quietly drop the rest.
              const [from, to] = monthRange(m.monthStart, asOfDate)

              return m.total > 0 ? (
                <Link
                  key={m.offset}
                  className="dashrun__col"
                  title={title}
                  to={registerLink({ expiryFrom: from, expiryTo: to })}
                >
                  {inner}
                </Link>
              ) : (
                <span key={m.offset} className="dashrun__col" title={title}>
                  {inner}
                </span>
              )
            })}
          </div>
        </div>

        <p className="dash__note">
          <strong>{total}</strong> renewals across the year. The{' '}
          <Link to={registerLink({ hasSeverity: Severity.Expired })}>
            {expired} already overdue
          </Link>{' '}
          are not shown here — a backlog is not a forecast, and a bar that size would
          flatten every other month.
        </p>
      </div>
    </section>
  )
}

/**
 * The severity mix, as a table.
 *
 * A table rather than a chart because every cell has to be clickable and exact.
 * The bar beside each row is for scanning, not for reading values off.
 */
function ObligationPanel({
  obligations,
  suspect,
}: {
  obligations: DashboardObligation[]
  suspect: Set<number>
}) {
  const BANDS = [
    { key: 'expiredCount', severity: Severity.Expired, tone: 'crit', label: 'Expired' },
    { key: 'dueSoonCount', severity: Severity.DueSoon, tone: 'warn', label: 'Due soon' },
    { key: 'validCount', severity: Severity.Valid, tone: 'ok', label: 'Valid' },
    { key: 'noExpiryCount', severity: Severity.NoExpiry, tone: 'none', label: 'Never expires' },
    { key: 'notApplicableCount', severity: Severity.NotApplicable, tone: 'na', label: 'N/A' },
    { key: 'unknownCount', severity: Severity.Unknown, tone: 'unk', label: 'Unknown' },
  ] as const

  return (
    <section className="dash__sec">
      <div className="dash__sech">
        <h2>By obligation</h2>
        <span className="eyebrow">every figure opens the register</span>
      </div>
      <p className="dash__cap">Click any number to see exactly those vehicles.</p>

      <div className="dash__card">
        <div className="dash__scroll">
          <table className="dashtbl">
            <thead>
              <tr>
                <th>Obligation</th>
                {BANDS.map((b) => (
                  <th key={b.key}>{b.label}</th>
                ))}
                <th className="dashtbl__mixh">Mix</th>
              </tr>
            </thead>
            <tbody>
              {obligations.map((o) => {
                const total = BANDS.reduce((n, b) => n + ((o[b.key] ?? 0) as number), 0)

                return (
                  <tr key={o.complianceTypeId}>
                    <td>
                      {o.displayName}
                      {suspect.has(o.complianceTypeId ?? 0) && (
                        <span className="dashtbl__flag" title="Looks like a stale import — see below">
                          stale
                        </span>
                      )}
                    </td>

                    {BANDS.map((b) => {
                      const n = (o[b.key] ?? 0) as number
                      if (!n) return <td key={b.key}><span className="zero">0</span></td>

                      return (
                        <td key={b.key}>
                          <Link
                            className={`dashtbl__n dashtbl__n--${b.tone}`}
                            to={registerLink({
                              obligationTypeId: o.complianceTypeId,
                              obligationSeverity: b.severity,
                            })}
                          >
                            {n}
                          </Link>
                        </td>
                      )
                    })}

                    <td className="dashtbl__mix">
                      <span className="dashbar" role="img" aria-label={`${o.displayName}: ${total} obligations`}>
                        {BANDS.map((b) => {
                          const n = (o[b.key] ?? 0) as number
                          if (!n) return null
                          return (
                            <span
                              key={b.key}
                              className={`dashbar__seg dashbar__seg--${b.tone}`}
                              style={{ width: `${(n / total) * 100}%` }}
                              title={`${b.label}: ${n}`}
                            />
                          )
                        })}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <ul className="dashkey">
          {BANDS.map((b) => (
            <li key={b.key}>
              <i className={`dashbar__seg--${b.tone}`} />
              {b.label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/**
 * Sites ranked by valid Fitness certificates.
 *
 * A rate, not a count — an absolute count merely ranks sites by how many vehicles
 * they run. Fitness because it is the obligation whose data is current enough to
 * rank on; the API says which those are.
 */
function SitePanel({ sites }: { sites: DashboardSite[] }) {
  // Under five vehicles, one vehicle swings the percentage from 0 to 100 and the
  // ranking says nothing. Counted and disclosed rather than silently dropped.
  const MIN = 5
  const ranked = sites
    .filter((s) => (s.vehicleCount ?? 0) >= MIN)
    .sort((a, b) => (a.fitnessValidRate ?? 0) - (b.fitnessValidRate ?? 0))

  const omitted = sites.filter((s) => (s.vehicleCount ?? 0) < MIN)
  const omittedVehicles = omitted.reduce((n, s) => n + (s.vehicleCount ?? 0), 0)

  if (ranked.length === 0) return null

  return (
    <section className="dash__sec">
      <div className="dash__sech">
        <h2>Fitness by site</h2>
        <span className="eyebrow">worst first</span>
      </div>
      <p className="dash__cap">
        Share of vehicles holding a valid Fitness certificate — the obligation whose
        dates are current enough to compare sites by.
      </p>

      <div className="dash__card">
        <div className="dashrank">
          {ranked.map((s) => {
            const rate = s.fitnessValidRate ?? 0
            const tone = rate >= 0.6 ? 'ok' : rate >= 0.3 ? 'warn' : 'crit'

            return (
              <Link
                key={s.siteId ?? 'none'}
                className="dashrank__row"
                to={registerLink({
                  siteId: s.siteId ?? undefined,
                  obligationTypeId: 3,
                  obligationSeverity: Severity.Expired,
                })}
                title={`${s.siteName ?? 'No site assigned'} — ${s.fitnessValid} of ${s.vehicleCount} valid`}
              >
                <span className="dashrank__nm">{s.siteName ?? 'No site assigned'}</span>
                <span className="dashrank__tr">
                  <span
                    className={`dashrank__fl dashrank__fl--${tone}`}
                    style={{ width: `${Math.max(rate * 100, 1.5)}%` }}
                  />
                </span>
                <span className="dashrank__v">
                  <b>{Math.round(rate * 100)}%</b> {s.fitnessValid}/{s.vehicleCount}
                </span>
              </Link>
            )
          })}
        </div>

        {omitted.length > 0 && (
          <p className="dash__note">
            {omitted.length} sites with fewer than {MIN} vehicles are omitted —{' '}
            {omittedVehicles} vehicles between them. One vehicle swings a percentage
            from 0 to 100.
          </p>
        )}
      </div>
    </section>
  )
}

/**
 * How far to trust the rest of the page.
 *
 * Every sentence and both numbers come from the API. The page does not decide what
 * is stale — that judgement is made once, in C#, so this screen and the bot and any
 * later report cannot disagree about it.
 */
/**
 * What VAHAN says about the fleet.
 *
 * Every figure here links into the filtered register, like the rest of this screen —
 * and unlike the two obligation tiles that carry an asterisk, these all CAN, because
 * the register gained the matching filters in the same change.
 *
 * ⚠️ Every panel states its denominator. Registry facts exist only for vehicles that
 * have been fetched, so a fuel mix totalling 609 beside a register that says 612
 * reads as a bug rather than as coverage. The register has always been stale in both
 * directions; a chart that hides its own coverage is how that stops being visible.
 */
function RegistryPanel({ registry }: { registry: DashboardRegistry }) {
  const covered = registry.covered ?? 0
  const total = registry.totalVehicles ?? 0
  const notCovered = total - covered

  if (covered === 0) return null

  const bars = (items: { value?: string; vehicleCount?: number }[], param: string) =>
    items.map((i) => (
      <li key={i.value}>
        <Link
          className="dashreg__row"
          to={registerLink({ [param]: i.value })}
          title={`Show the ${i.vehicleCount} vehicles`}
        >
          <span className="dashreg__k">{i.value}</span>
          <span className="dashreg__bar">
            <i style={{ width: `${covered ? ((i.vehicleCount ?? 0) / covered) * 100 : 0}%` }} />
          </span>
          <span className="dashreg__n mono">{i.vehicleCount}</span>
        </Link>
      </li>
    ))

  const finance = registry.finance
  const notActive = registry.rcNotActive ?? 0

  return (
    <section className="dash__sec">
      <div className="dash__sech">
        <h2>What the RTO says</h2>
        <span className="eyebrow">live VAHAN data</span>
      </div>
      <p className="dash__cap">
        From {covered.toLocaleString()} of {total.toLocaleString()} vehicles.
        {notCovered > 0 && (
          <>
            {' '}
            The other {notCovered.toLocaleString()} have never been fetched, so nothing
            below counts them.
          </>
        )}
      </p>

      <div className="dash__card">
        <div className="dashreg">
          <div className="dashreg__col">
            <h3 className="eyebrow">
              RC status
              {/* Led with, because it is the only figure here that is a live
                  compliance problem rather than a description. */}
              {notActive > 0 && <span className="dashreg__alert">{notActive} not active</span>}
            </h3>
            <ul>{bars(registry.rcStatuses ?? [], 'rcStatus')}</ul>
          </div>

          <div className="dashreg__col">
            <h3 className="eyebrow">Fuel</h3>
            <ul>{bars(registry.fuelTypes ?? [], 'fuelType')}</ul>
          </div>

          <div className="dashreg__col">
            <h3 className="eyebrow">Emission norms</h3>
            <ul>{bars(registry.emissionNorms ?? [], 'emissionNorms')}</ul>
          </div>

          <div className="dashreg__col">
            <h3 className="eyebrow">Finance</h3>
            <ul>
              <li>
                <Link className="dashreg__row" to={registerLink({ isFinanced: 'true' })}>
                  <span className="dashreg__k">Financed</span>
                  <span className="dashreg__bar">
                    <i style={{ width: `${covered ? ((finance?.financed ?? 0) / covered) * 100 : 0}%` }} />
                  </span>
                  <span className="dashreg__n mono">{finance?.financed ?? 0}</span>
                </Link>
              </li>
              <li>
                <Link className="dashreg__row" to={registerLink({ isFinanced: 'false' })}>
                  <span className="dashreg__k">Owned outright</span>
                  <span className="dashreg__bar">
                    <i style={{ width: `${covered ? ((finance?.notFinanced ?? 0) / covered) * 100 : 0}%` }} />
                  </span>
                  <span className="dashreg__n mono">{finance?.notFinanced ?? 0}</span>
                </Link>
              </li>
              {/*
                Not a link, and not folded into "owned outright": the registry never
                said. Reporting these as unfinanced would be inventing a fact.
              */}
              {(finance?.notStated ?? 0) > 0 && (
                <li>
                  <span className="dashreg__row dashreg__row--flat">
                    <span className="dashreg__k">Not stated</span>
                    <span className="dashreg__bar" />
                    <span className="dashreg__n mono">{finance?.notStated}</span>
                  </span>
                </li>
              )}
            </ul>

            {(registry.topFinanciers?.length ?? 0) > 0 && (
              <>
                <h3 className="eyebrow">Financiers</h3>
                <ul>{bars(registry.topFinanciers ?? [], 'financer')}</ul>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function TrustPanel({ findings, data }: { findings: DashboardFinding[]; data: DashboardOverview }) {
  if (findings.length === 0) return null

  const staleness = data.staleness ?? []

  return (
    <section className="dash__sec" id="trust">
      <div className="dash__sech">
        <h2>How far to trust it</h2>
        <span className="eyebrow">read before acting</span>
      </div>
      <p className="dash__cap">
        Not every column is equally reliable. These are the register&rsquo;s own gaps,
        stated plainly.
      </p>

      <div className="dash__card">
        <ul className="dashfind">
          {findings.map((f, i) => (
            <li key={i} className={`dashfind__i dashfind__i--${f.kind}`}>
              <span className="dashfind__h">{f.headline}</span>
              <span className="dashfind__d">{f.detail}</span>
            </li>
          ))}
        </ul>

        {staleness.length > 0 && (
          <div className="dash__scroll">
            <table className="dashtbl dashtbl--tight">
              <thead>
                <tr>
                  <th>Obligation</th>
                  <th>Expired</th>
                  <th>Still valid</th>
                  <th>Certificates</th>
                  <th className="dashtbl__vh">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {staleness.map((s) => {
                  const o = (data.obligations ?? []).find(
                    (x) => x.complianceTypeId === s.complianceTypeId,
                  )
                  const live = Math.round((s.liveShare ?? 0) * 100)

                  return (
                    <tr key={s.complianceTypeId}>
                      <td>{s.displayName}</td>
                      <td>{cell(s.totalExpired ?? 0, 'crit')}</td>
                      <td className="mono">{live}%</td>
                      <td className="mono">{o?.acceptsDocument ? (o.documentCount ?? 0) : '—'}</td>
                      <td className="dashtbl__v">
                        {s.isStaleSnapshot ? (
                          <span className="dashverdict dashverdict--bad">Stale snapshot</span>
                        ) : (s.liveShare ?? 0) < 0.1 ? (
                          <span className="dashverdict dashverdict--warn">Nothing current</span>
                        ) : (
                          <span className="dashverdict dashverdict--ok">Being maintained</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="dash__note">
          &ldquo;&mdash;&rdquo; means the obligation takes no certificate. &ldquo;Still
          valid&rdquo; is the share of the fleet whose date is in the future — the figure
          that separates a maintained column from a year-old snapshot.
        </p>
      </div>
    </section>
  )
}

function formatDate(iso: string | undefined): string {
  if (!iso) return ''
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}-${m}-${y}`
}
